/* ==========================================================================
   FC Cart Drawer — behavior layer on top of the theme's cart plumbing.

   The theme's theme.CartDrawer / theme.CartForm still own open/close,
   quantity changes and item markup rebuilds ([data-products] is replaced
   with /cart?view=ajax markup). This script only:
     - syncs the header item count
     - drives the FC gift progress bar (fill, milestones, state messages)
     - toggles the footer shipping-fee / free-shipping / free-gift line
     - handles the item remove (X) buttons
     - handles quick-add on the More From Collection cards
     - relays "Choose your free gift here!" clicks (fc:gift-popup:open)
   ========================================================================== */
(function() {
  'use strict';

  var drawer;
  var docBound = false;

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function init() {
    drawer = document.getElementById('CartDrawer');
    if (!drawer || !drawer.classList.contains('fc-cart-drawer')) {
      return;
    }

    var products = drawer.querySelector('[data-products]');
    if (products) {
      // Fires after every CartForm.buildCart() rebuild (initial load,
      // quantity change, ajax add) — the single sync point for all triggers.
      new MutationObserver(syncFromMarkup).observe(products, { childList: true });
    }

    if (!docBound) {
      docBound = true;
      document.addEventListener('cart:updated', function(evt) {
        var cart = evt.detail && evt.detail.cart;
        if (!cart || typeof cart.total_price !== 'number') {
          return;
        }
        var giftTotal = 0;
        var hasGift = false;
        (cart.items || []).forEach(function(item) {
          if (item.properties && item.properties._fc_free_gift) {
            giftTotal += item.final_line_price * 1;
            hasGift = true;
          }
        });
        apply(cart.item_count, cart.total_price, giftTotal, hasGift);
      });
    }

    drawer.addEventListener('click', onClick);

    initRecsDrag();
    initGiftPopup();
    initQuickAdd();
    initWishlist();

    // The quick add / size guide live on top of the drawer — whenever the
    // drawer closes (its X button, Esc, cart:close, anything), they must go
    // with it. Watching the class covers every close path in one place.
    if (!drawer.dataset.fcCloseObserved) {
      drawer.dataset.fcCloseObserved = 'true';
      new MutationObserver(function() {
        if (!drawer.classList.contains('drawer--is-open')) {
          closeQuickAdd();
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    apply(
      parseInt(drawer.dataset.initialCount, 10) || 0,
      parseInt(drawer.dataset.initialSubtotal, 10) || 0,
      parseInt(drawer.dataset.initialGiftTotal, 10) || 0,
      drawer.dataset.initialHasGift === 'true'
    );
  }

  function syncFromMarkup() {
    var items = drawer.querySelector('.cart__items');
    if (!items) {
      return;
    }
    apply(
      parseInt(items.dataset.count, 10) || 0,
      parseInt(items.dataset.cartSubtotal, 10) || 0,
      parseInt(items.dataset.giftTotal, 10) || 0,
      !!drawer.querySelector('[data-products] [data-fc-gift]')
    );
    updateRecCards();
  }

  /* --------------------------------------------------------------------
     Wishlist hearts — official Swym JS SDK pattern (developers.getswym.com):
     SwymCallbacks hands us `swat`; swat.fetch marks already-saved products
     (keyed by empi); clicks toggle add/removeFromWishList with {epi, empi, du}.
     -------------------------------------------------------------------- */
  var fcSwat = null;
  var wishlistInit = false;

  function initWishlist() {
    if (wishlistInit) {
      return;
    }
    wishlistInit = true;
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function(swat) {
      fcSwat = swat;
      swat.fetch(function(items) {
        var saved = {};
        (items || []).forEach(function(item) {
          saved[item.empi] = true;
        });
        drawer.querySelectorAll('[data-fc-wish]').forEach(function(btn) {
          var added = !!saved[btn.dataset.productId];
          btn.classList.toggle('swym-added', added);
          btn.setAttribute('aria-pressed', added ? 'true' : 'false');
        });
      });
    });
  }

  function toggleWishlist(btn) {
    if (!fcSwat) {
      return;
    }
    var product = {
      epi: parseInt(btn.dataset.variantId, 10),
      empi: parseInt(btn.dataset.productId, 10),
      du: btn.dataset.productUrl
    };
    var wasAdded = btn.classList.contains('swym-added');
    var onSuccess = function() {
      btn.classList.toggle('swym-added', !wasAdded);
      btn.setAttribute('aria-pressed', wasAdded ? 'false' : 'true');
    };
    if (wasAdded) {
      fcSwat.removeFromWishList(product, onSuccess);
    } else {
      fcSwat.addToWishList(product, onSuccess);
    }
  }

  /* Hide More From Collection cards whose product is already in the bag;
     cards come back when the item is removed. Reads product ids straight
     from the rebuilt cart markup, so every rebuild path is covered. */
  function updateRecCards() {
    var cards = drawer.querySelectorAll('.fc-rec-card[data-product-id]');
    if (!cards.length) {
      return;
    }
    var inCart = {};
    drawer.querySelectorAll('[data-products] [data-product-id]').forEach(function(el) {
      inCart[el.dataset.productId] = true;
    });
    cards.forEach(function(card) {
      card.classList.toggle('is-in-cart', inCart[card.dataset.productId] === true);
    });
  }

  function apply(count, subtotal, giftTotal, hasGift) {
    updateCount(count);
    // Free-gift lines don't count toward the reward thresholds
    var progressTotal = subtotal - (giftTotal || 0);
    updateProgress(progressTotal);
    // CSS keys off this: in state 2 WITHOUT a chosen gift, the progress block
    // turns clickable and the gift milestone pulses to invite the pick.
    drawer.classList.toggle('fc-has-gift', !!hasGift);
    syncGiftState(progressTotal, !!hasGift);
  }

  /* --------------------------------------------------------------------
     Shipping fee note — DO NOT fetch live rates here.
     /cart/shipping_rates.json (and prepare_/async_shipping_rates) is NOT a
     read-only quote: Shopify PERSISTS whatever shipping_address you pass onto
     the cart, which then pre-fills the checkout address. A hard-coded
     placeholder zip used for quoting silently stamped "1012 AB" onto NL carts
     and shipped 7 orders to the wrong postcode (incident 2026-10-19). There is
     no storefront rate endpoint that quotes without mutating the cart address.
     The below-threshold note is therefore a static "calculated at checkout"
     string rendered server-side in fc-cart-drawer.liquid — no address effect.
     -------------------------------------------------------------------- */

  function updateCount(count) {
    var countEl = drawer.querySelector('[data-fc-count]');
    var wordEl = drawer.querySelector('[data-fc-count-word]');

    if (countEl) {
      countEl.textContent = count;
    }
    if (wordEl) {
      wordEl.textContent = count === 1 ? wordEl.dataset.singular : wordEl.dataset.plural;
    }
  }

  function updateProgress(subtotal) {
    var progress = drawer.querySelector('[data-fc-progress]');
    if (!progress) {
      return;
    }

    var ship = parseInt(progress.dataset.shippingThreshold, 10);
    var gift = parseInt(progress.dataset.giftThreshold, 10);
    var shipPos = parseFloat(progress.dataset.shipPos) || 50;
    var state = subtotal >= gift ? 2 : subtotal >= ship ? 1 : 0;

    drawer.classList.remove('fc-state-0', 'fc-state-1', 'fc-state-2');
    drawer.classList.add('fc-state-' + state);

    var pct;
    if (state === 2) {
      pct = 100;
    } else if (state === 1) {
      pct = shipPos + ((subtotal - ship) / (gift - ship)) * (100 - shipPos);
    } else {
      pct = (subtotal / ship) * shipPos;
    }

    var fill = progress.querySelector('[data-fc-progress-fill]');
    if (fill) {
      fill.style.width = Math.min(100, Math.max(0, pct)) + '%';
    }

    var shipMilestone = progress.querySelector('[data-fc-milestone="shipping"]');
    var giftMilestone = progress.querySelector('[data-fc-milestone="gift"]');
    if (shipMilestone) {
      shipMilestone.classList.toggle('is-unlocked', state >= 1);
    }
    if (giftMilestone) {
      giftMilestone.classList.toggle('is-unlocked', state === 2);
    }

    progress.querySelectorAll('[data-fc-amount="shipping"]').forEach(function(el) {
      el.textContent = formatMoney(Math.max(0, ship - subtotal));
    });
    progress.querySelectorAll('[data-fc-amount="gift"]').forEach(function(el) {
      el.textContent = formatMoney(Math.max(0, gift - subtotal));
    });
  }

  /* --------------------------------------------------------------------
     Free gift — popup + cart-state sync
     -------------------------------------------------------------------- */
  var removingGift = false;

  function giftThreshold() {
    var progress = drawer.querySelector('[data-fc-progress]');
    return progress ? parseInt(progress.dataset.giftThreshold, 10) : NaN;
  }

  function giftLineKeys() {
    return Array.prototype.map.call(
      drawer.querySelectorAll('[data-products] [data-fc-gift]'),
      function(el) { return el.dataset.key; }
    );
  }

  function syncGiftState(progressTotal, hasGift) {
    var threshold = giftThreshold();
    if (isNaN(threshold)) {
      return;
    }

    // Cart dropped below the gift threshold → the gift is no longer earned
    if (hasGift && progressTotal < threshold && !removingGift) {
      var keys = giftLineKeys();
      if (keys.length) {
        removingGift = true;
        keys
          .reduce(function(chain, key) {
            return chain.then(function() { return theme.cart.changeItem(key, 0); });
          }, Promise.resolve())
          .then(function() {
            removingGift = false;
            document.dispatchEvent(new CustomEvent('cart:build'));
          })
          .catch(function() { removingGift = false; });
      }
      return;
    }

    // Gift just unlocked → auto-open the selector once per session
    // (per design annotation), only while the drawer is visible.
    if (
      progressTotal >= threshold &&
      !hasGift &&
      document.getElementById('FCGiftPopup') &&
      drawer.classList.contains('drawer--is-open') &&
      !sessionStorage.getItem('fcGiftAutoShown')
    ) {
      sessionStorage.setItem('fcGiftAutoShown', '1');
      openGiftPopup();
    }
  }

  function openGiftPopup() {
    // Legacy (qikify) gift block: the app owns the selector popup — click
    // its gift milestone instead. Covers every entry point (footer link,
    // progress link, whole-block click) since they all funnel through here.
    if (drawer.classList.contains('fc-gift-legacy')) {
      var appGift = drawer.querySelector('.qbk-order-goal__milestone--gift');
      if (appGift) {
        appGift.click();
      }
      return;
    }
    var popup = document.getElementById('FCGiftPopup');
    if (!popup) {
      return;
    }
    // Per annotation: any open overlay (cart drawer, quick add, nav) must
    // close first — 'modalOpen' is the theme's own "close everything" signal
    // (theme.Drawers and theme.Modals both listen for it).
    document.dispatchEvent(new CustomEvent('modalOpen'));
    popup.hidden = false;
    // modalOpen just closed the drawer, dropping html.js-drawer-open — the
    // class that hides the chat widget (z-index 2147483000, can't be beaten
    // with z-index). Mirror the popup's open state so the chat stays hidden.
    document.documentElement.classList.add('fc-gift-open');
    var panel = popup.querySelector('.fc-gift__panel');
    if (panel) {
      panel.focus();
    }
  }

  // Per annotation: "when clicked close, cart has to popup" — every close
  // path reopens the drawer unless the caller opts out (Continue shopping,
  // or the add flow where ajaxProduct:added opens it anyway).
  function closeGiftPopup(reopenCart) {
    var popup = document.getElementById('FCGiftPopup');
    if (!popup || popup.hidden) {
      return;
    }
    popup.hidden = true;
    document.documentElement.classList.remove('fc-gift-open');
    if (reopenCart !== false) {
      document.dispatchEvent(new CustomEvent('cart:open'));
      var giftLink = drawer.querySelector('[data-fc-gift-link]');
      if (giftLink) {
        giftLink.focus();
      }
    }
  }

  // Registered OUTSIDE initGiftPopup: in Legacy (qikify) gift-block mode the
  // FC popup element doesn't exist, so initGiftPopup bails early — but gift
  // links must still open the APP's popup via openGiftPopup's legacy branch.
  var giftOpenBound = false;

  function bindGiftOpen() {
    if (giftOpenBound) {
      return;
    }
    giftOpenBound = true;
    document.addEventListener('fc:gift-popup:open', openGiftPopup);
  }

  function initGiftPopup() {
    bindGiftOpen();
    var popup = document.getElementById('FCGiftPopup');
    if (!popup || popup.dataset.fcBound) {
      return;
    }
    popup.dataset.fcBound = 'true';

    var cta = popup.querySelector('[data-fc-gift-cta]');
    var giftLimit = parseInt(popup.dataset.giftLimit, 10) || 1;
    var chosenCards = [];
    document.addEventListener('keydown', function(evt) {
      if (evt.key === 'Escape' && !popup.hidden) {
        closeGiftPopup();
      }
    });

    function closeAllMenus(except) {
      popup.querySelectorAll('[data-fc-gift-size-menu]').forEach(function(menu) {
        if (menu !== except) {
          menu.hidden = true;
          menu.closest('[data-fc-gift-size]')
            .querySelector('[data-fc-gift-size-toggle]')
            .setAttribute('aria-expanded', 'false');
        }
      });
    }

    function updateCta() {
      var ready = chosenCards.length > 0;
      cta.classList.toggle('is-locked', !ready);
      cta.setAttribute('aria-disabled', ready ? 'false' : 'true');
      cta.textContent = ready ? cta.dataset.continueLabel : cta.dataset.pickLabel;
    }

    function toggleCard(card) {
      var idx = chosenCards.indexOf(card);
      if (idx > -1) {
        card.classList.remove('is-chosen');
        chosenCards.splice(idx, 1);
      } else {
        // At the limit: the oldest pick makes room (radio behavior at limit 1)
        if (chosenCards.length >= giftLimit) {
          chosenCards.shift().classList.remove('is-chosen');
        }
        card.classList.add('is-chosen');
        chosenCards.push(card);
      }
      updateCta();
    }

    function shakeCta() {
      cta.classList.remove('fc-shake');
      // restart the animation even on rapid re-clicks
      void cta.offsetWidth;
      cta.classList.add('fc-shake');
    }
    cta.addEventListener('animationend', function() {
      cta.classList.remove('fc-shake');
    });

    // contain clicks so theme.Drawers' window listener never closes a
    // reopened drawer underneath (overlays are siblings of #CartDrawer)
    popup.addEventListener('click', function(evt) {
      evt.stopPropagation();
    });

    popup.addEventListener('click', function(evt) {
      if (evt.target.closest('[data-fc-gift-close]')) {
        closeGiftPopup();
        return;
      }

      var toggle = evt.target.closest('[data-fc-gift-size-toggle]');
      if (toggle) {
        var menu = toggle.closest('[data-fc-gift-size]').querySelector('[data-fc-gift-size-menu]');
        var willOpen = menu.hidden;
        closeAllMenus(null);
        menu.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        return;
      }

      var option = evt.target.closest('[data-fc-gift-size-option]');
      if (option) {
        var card = option.closest('[data-fc-gift-card]');
        card.dataset.variantId = option.dataset.variantId;
        var valueEl = card.querySelector('[data-fc-gift-size-value]');
        if (valueEl) {
          valueEl.textContent = option.textContent.trim();
        }
        card.classList.add('has-size');
        closeAllMenus(null);
        return;
      }

      var choose = evt.target.closest('[data-fc-gift-choose]');
      if (choose) {
        var chooseCard = choose.closest('[data-fc-gift-card]');
        if (!chooseCard.dataset.variantId) {
          // No size picked yet — open that card's size menu as the nudge
          var sizeToggle = chooseCard.querySelector('[data-fc-gift-size-toggle]');
          if (sizeToggle) {
            sizeToggle.click();
          }
          return;
        }
        toggleCard(chooseCard);
        return;
      }

      if (evt.target.closest('[data-fc-gift-cta]')) {
        if (cta.classList.contains('is-locked')) {
          // Nothing picked yet — per annotation, nudge with a slight shake
          shakeCta();
          return;
        }
        if (chosenCards.length) {
          addGift(chosenCards.map(function(card) { return card.dataset.variantId; }));
        }
      }
    });

    function addGift(variantIds) {
      if (cta.classList.contains('is-loading')) {
        return;
      }
      cta.classList.add('is-loading');

      // Replace any previously chosen gifts, then add the new picks
      giftLineKeys()
        .reduce(function(chain, key) {
          return chain.then(function() { return theme.cart.changeItem(key, 0); });
        }, Promise.resolve())
        .then(function() {
          return variantIds.reduce(function(chain, variantId) {
            return chain.then(function() {
              return fetch(theme.routes.cartAdd, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                  id: parseInt(variantId, 10),
                  quantity: 1,
                  properties: { _fc_free_gift: 'true' }
                })
              }).then(function(response) { return response.json(); });
            });
          }, Promise.resolve(null));
        })
        .then(function(data) {
          cta.classList.remove('is-loading');
          if (data && data.status && data.status !== 200) {
            alert(data.description || data.message);
            return;
          }
          closeGiftPopup(false);
          // Rebuilds the drawer and opens it ("the cart with added freebie
          // should pop up" per the design annotation)
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
        })
        .catch(function() {
          cta.classList.remove('is-loading');
        });
    }
  }

  /* Desktop drag-to-scroll for the More From Collection track.
     Mouse only — touch scrolls natively. A drag beyond 6px suppresses the
     click that follows it (capture phase), so plain clicks on cards,
     wishlist hearts and quick-add buttons keep working. */
  function initRecsDrag() {
    var track = drawer.querySelector('.fc-recs__track');
    if (!track || track.dataset.fcDragBound) {
      return;
    }
    track.dataset.fcDragBound = 'true';

    var startX = 0;
    var startScroll = 0;
    var pointerId = null;
    var dragged = false;

    track.addEventListener('pointerdown', function(evt) {
      if (evt.pointerType !== 'mouse' || evt.button !== 0) {
        return;
      }
      pointerId = evt.pointerId;
      startX = evt.clientX;
      startScroll = track.scrollLeft;
      dragged = false;
    });

    track.addEventListener('pointermove', function(evt) {
      if (evt.pointerId !== pointerId) {
        return;
      }
      var delta = evt.clientX - startX;
      if (!dragged && Math.abs(delta) > 6) {
        dragged = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(pointerId);
      }
      if (dragged) {
        track.scrollLeft = startScroll - delta;
        evt.preventDefault();
      }
    });

    function endDrag(evt) {
      if (evt.pointerId !== pointerId) {
        return;
      }
      pointerId = null;
      track.classList.remove('is-dragging');
      // `dragged` stays true until the click below, then resets.
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // A click that ends a drag must not navigate / add to cart.
    track.addEventListener('click', function(evt) {
      if (dragged) {
        dragged = false;
        evt.preventDefault();
        evt.stopPropagation();
      }
    }, true);

    // Stop native image/link ghost-drag from hijacking the gesture.
    track.addEventListener('dragstart', function(evt) {
      evt.preventDefault();
    });
  }

  function onClick(evt) {
    var wishBtn = evt.target.closest('[data-fc-wish]');
    if (wishBtn) {
      evt.preventDefault();
      toggleWishlist(wishBtn);
      return;
    }

    var removeBtn = evt.target.closest('[data-fc-remove]');
    if (removeBtn) {
      evt.preventDefault();
      var item = removeBtn.closest('[data-key]');
      if (item) {
        item.classList.add('is-removing');
      }
      // Can't reuse the cart:quantity event here: theme.CartForm's
      // quantityChanged guard (`if (!key || !qty) return`) drops qty 0.
      // Change the line directly, then let the theme rebuild the markup.
      theme.cart.changeItem(removeBtn.dataset.key, 0)
        .then(function(cartText) {
          document.dispatchEvent(new CustomEvent('cart:build'));
          try {
            var cart = JSON.parse(cartText);
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          } catch (e) { /* markup rebuild still syncs via the MutationObserver */ }
        })
        .catch(function() {
          if (item) {
            item.classList.remove('is-removing');
          }
        });
      return;
    }

    var giftLink = evt.target.closest('[data-fc-gift-link]');
    if (giftLink) {
      evt.preventDefault();
      // Gift selector popup ships separately — this is its open signal.
      document.dispatchEvent(new CustomEvent('fc:gift-popup:open'));
      return;
    }

    // Gift unlocked but not chosen yet: the ENTIRE progress block opens the
    // selector (bigger target for customers who come back and forgot). The
    // underlined link above stays the accessible/keyboard control.
    if (
      evt.target.closest('[data-fc-progress]') &&
      drawer.classList.contains('fc-state-2') &&
      !drawer.classList.contains('fc-has-gift')
    ) {
      document.dispatchEvent(new CustomEvent('fc:gift-popup:open'));
      return;
    }

    var quickAdd = evt.target.closest('[data-fc-quick-add]');
    if (quickAdd) {
      evt.preventDefault();
      quickAddToCart(quickAdd);
      return;
    }

    var qaOpen = evt.target.closest('[data-fc-qa-open]');
    if (qaOpen) {
      evt.preventDefault();
      var jsonEl = qaOpen.closest('[data-fc-gift-card], .fc-rec-card').querySelector('[data-fc-qa-json]');
      if (jsonEl) {
        try {
          openQuickAdd(JSON.parse(jsonEl.textContent));
        } catch (e) { /* malformed payload — leave the card link behavior */ }
      }
    }
  }

  /* --------------------------------------------------------------------
     Quick Add sheet (products with options) + Size guide
     -------------------------------------------------------------------- */
  var qa = {
    product: null,
    selections: [],
    sizeOptionIndex: -1
  };
  // iOS sheet motion: slide up on open, slide down on close
  var QA_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  var qaMotionOff = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var qaCloseTimer = null;

  function isColorOption(name) {
    return /^(color|colour|kleur)$/i.test(name);
  }
  function isSizeOption(name) {
    return /^(size|maat)$/i.test(name);
  }

  function qaEl() { return document.getElementById('FCQuickAdd'); }
  function sgEl() { return document.getElementById('FCSizeGuide'); }

  function optionValueName(value) {
    return typeof value === 'string' ? value : value.name;
  }

  function qaResolveVariant() {
    return (qa.product.variants || []).find(function(v) {
      return v.options.every(function(optValue, i) {
        return qa.selections[i] === optValue;
      });
    }) || null;
  }

  function qaVariantImageFor(optionIndex, value) {
    var withImage = (qa.product.variants || []).find(function(v) {
      return v.options[optionIndex] === value && v.image;
    });
    return withImage ? withImage.image : qa.product.image;
  }

  function qaValueAvailable(optionIndex, value) {
    // Available if any variant with this value matches the other selections
    return (qa.product.variants || []).some(function(v) {
      if (!v.available || v.options[optionIndex] !== value) {
        return false;
      }
      return v.options.every(function(optValue, i) {
        return i === optionIndex || !qa.selections[i] || qa.selections[i] === optValue;
      });
    });
  }

  function openQuickAdd(product) {
    var sheet = qaEl();
    if (!sheet) {
      return;
    }
    qa.product = product;
    qa.sizeOptionIndex = -1;
    qa.selections = (product.options || []).map(function(opt, i) {
      if (isSizeOption(opt.name)) {
        qa.sizeOptionIndex = i;
      }
      // default: first available value of each option
      var values = opt.values || [];
      for (var j = 0; j < values.length; j++) {
        var name = optionValueName(values[j]);
        if ((product.variants || []).some(function(v) { return v.available && v.options[i] === name; })) {
          return name;
        }
      }
      return values.length ? optionValueName(values[0]) : null;
    });

    var badge = sheet.querySelector('[data-fc-qa-badge]');
    badge.textContent = product.badge || '';
    badge.hidden = !product.badge;
    var title = sheet.querySelector('[data-fc-qa-title]');
    title.textContent = product.title;
    title.href = product.url;

    qaRenderOptions();
    qaSyncVariant();

    qaPositionSheet();

    if (qaCloseTimer) {
      window.clearTimeout(qaCloseTimer);
      qaCloseTimer = null;
    }
    delete sheet.dataset.fcClosing;

    if (qaMotionOff) {
      sheet.style.transition = '';
      sheet.style.transform = '';
      sheet.hidden = false;
    } else {
      // slide up from below the drawer edge
      sheet.style.transition = 'none';
      sheet.style.transform = 'translateY(100%)';
      sheet.hidden = false;
      void sheet.offsetHeight; // commit the start position
      sheet.style.transition = 'transform 0.4s ' + QA_EASE;
      sheet.style.transform = '';
      window.setTimeout(function() {
        sheet.style.transition = '';
      }, 420);
    }

    var panel = sheet.querySelector('.fc-qa__panel');
    panel.focus();
  }

  /* The sheet covers the drawer column from below the ENTIRE gift block
     (message + bar stay crisp, per Figma) — falls back to below the header
     when the gift bar is disabled. The backdrop's mask then feathers the
     blur in from this edge, so there is no hard line. */
  function qaPositionSheet() {
    var sheet = qaEl();
    if (!sheet) {
      return;
    }
    var anchor = drawer.querySelector('.fc-progress') || drawer.querySelector('.fc-cart__header');
    var top = 0;
    if (anchor) {
      top = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom));
    }
    sheet.style.top = top + 'px';
  }

  function closeQuickAdd() {
    var sheet = qaEl();
    closeSizeGuide();
    if (!sheet || sheet.hidden || sheet.dataset.fcClosing) {
      return;
    }
    if (qaMotionOff) {
      sheet.hidden = true;
      return;
    }
    // slide down from wherever the sheet currently is (incl. mid-drag)
    sheet.dataset.fcClosing = 'true';
    sheet.style.transition = 'transform 0.3s ' + QA_EASE;
    sheet.style.transform = 'translateY(100%)';
    qaCloseTimer = window.setTimeout(function() {
      qaCloseTimer = null;
      delete sheet.dataset.fcClosing;
      sheet.hidden = true;
      sheet.style.transition = '';
      sheet.style.transform = '';
    }, 300);
  }

  function closeSizeGuide() {
    var guide = sgEl();
    if (guide) {
      guide.hidden = true;
    }
  }

  function qaRenderOptions() {
    var wrap = qaEl().querySelector('[data-fc-qa-options]');
    wrap.innerHTML = '';

    (qa.product.options || []).forEach(function(opt, i) {
      var row = document.createElement('div');
      row.className = 'fc-qa__option';

      var head = document.createElement('div');
      head.className = 'fc-qa__option-head';

      if (isColorOption(opt.name)) {
        head.innerHTML = '<span class="fc-qa__option-label">' + (qaEl().dataset.colorLabel || opt.name) + '</span>' +
          '<span class="fc-qa__option-value" data-fc-qa-value-of="' + i + '"></span>';
        row.appendChild(head);

        var swatches = document.createElement('div');
        swatches.className = 'fc-qa__swatches';
        (opt.values || []).forEach(function(rawValue) {
          var value = optionValueName(rawValue);
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'fc-qa__swatch';
          btn.dataset.fcQaOption = i;
          btn.dataset.value = value;
          btn.setAttribute('aria-label', value);
          var img = document.createElement('img');
          img.src = qaVariantImageFor(i, value);
          img.alt = value;
          img.loading = 'lazy';
          btn.appendChild(img);
          swatches.appendChild(btn);
        });
        row.appendChild(swatches);
      } else {
        var isSize = i === qa.sizeOptionIndex;
        head.innerHTML = '<span class="fc-qa__option-label">' + (isSize ? (qaEl().dataset.sizeLabel || opt.name) : opt.name) + '</span>' +
          (isSize && qaEl().dataset.fitNote ? '<span class="fc-qa__option-note">' + qaEl().dataset.fitNote + '</span>' : '') +
          (isSize && qaEl().dataset.sizeHelp ? '<button type="button" class="fc-qa__size-help" data-fc-qa-size-help>' + qaEl().dataset.sizeHelp + '</button>' : '');
        row.appendChild(head);

        var pills = document.createElement('div');
        pills.className = 'fc-qa__pills';
        (opt.values || []).forEach(function(rawValue) {
          var value = optionValueName(rawValue);
          var pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'fc-qa__pill';
          pill.dataset.fcQaOption = i;
          pill.dataset.value = value;
          pill.textContent = value;
          pills.appendChild(pill);
        });
        row.appendChild(pills);
      }

      wrap.appendChild(row);
    });

    qaRefreshOptionStates();
  }

  function qaRefreshOptionStates() {
    var sheet = qaEl();
    sheet.querySelectorAll('[data-fc-qa-option]').forEach(function(el) {
      var i = parseInt(el.dataset.fcQaOption, 10);
      el.classList.toggle('is-selected', qa.selections[i] === el.dataset.value);
      el.classList.toggle('is-unavailable', !qaValueAvailable(i, el.dataset.value));
    });
    sheet.querySelectorAll('[data-fc-qa-value-of]').forEach(function(el) {
      el.textContent = qa.selections[parseInt(el.dataset.fcQaValueOf, 10)] || '';
    });
  }

  function qaSyncVariant() {
    var sheet = qaEl();
    var variant = qaResolveVariant();
    var img = sheet.querySelector('[data-fc-qa-image]');
    img.src = (variant && variant.image) || qa.product.image || '';
    img.alt = qa.product.title;

    var price = sheet.querySelector('[data-fc-qa-price]');
    var compare = sheet.querySelector('[data-fc-qa-compare]');
    if (variant) {
      price.textContent = formatMoney(variant.price);
      var hasCompare = variant.compare > variant.price;
      compare.hidden = !hasCompare;
      if (hasCompare) {
        compare.textContent = formatMoney(variant.compare);
      }
    }

    var addBtn = sheet.querySelector('[data-fc-qa-add]');
    addBtn.classList.toggle('is-locked', !variant || !variant.available);
  }

  function qaAddSelected(btn) {
    var variant = qaResolveVariant();
    if (!variant || !variant.available || (btn && btn.classList.contains('is-loading'))) {
      return;
    }
    if (btn) {
      btn.classList.add('is-loading');
    }
    fetch(theme.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: variant.id, quantity: 1 })
    })
      .then(function(response) { return response.json(); })
      .then(function(data) {
        if (btn) {
          btn.classList.remove('is-loading');
        }
        if (data.status && data.status !== 200) {
          alert(data.description || data.message);
          return;
        }
        closeQuickAdd();
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
      })
      .catch(function() {
        if (btn) {
          btn.classList.remove('is-loading');
        }
      });
  }

  /* Size guide: recommendation from the rendered chart table.
     Fixed column order: Size | Height | Chest | Waist.
     Nearest-match by distance: a measurement inside a size's range adds 0,
     outside adds how far off it is — so out-of-range values still recommend
     the closest size instead of nothing. Only sizes the current product
     actually offers are considered (the chart may list more). Ties go to
     the smaller size (chart order). */
  function sgCalcRecommendation() {
    var guide = sgEl();
    var inputs = {};
    var hasInput = false;
    guide.querySelectorAll('[data-fc-sg-input]').forEach(function(input) {
      var value = parseFloat(input.value) || 0;
      inputs[input.dataset.fcSgInput] = value;
      if (value > 0) {
        hasInput = true;
      }
    });

    var recoEl = guide.querySelector('[data-fc-sg-reco]');
    if (!hasInput) {
      recoEl.textContent = '';
      return;
    }

    var offered = {};
    var offersAny = false;
    guide.querySelectorAll('[data-fc-sg-sizes] .fc-qa__pill').forEach(function(pill) {
      offered[pill.dataset.value.toLowerCase()] = true;
      offersAny = true;
    });

    var best = null;
    var bestDist = Infinity;
    guide.querySelectorAll('[data-fc-sg-table] tbody tr').forEach(function(tr) {
      var cells = tr.children;
      if (cells.length < 4) {
        return;
      }
      var size = cells[0].textContent.trim();
      if (offersAny && !offered[size.toLowerCase()]) {
        return;
      }
      var ranges = {
        height: cells[1].textContent,
        chest: cells[2].textContent,
        waist: cells[3].textContent
      };
      var dist = 0;
      ['height', 'chest', 'waist'].forEach(function(key) {
        if (!inputs[key]) {
          return;
        }
        var match = ranges[key].match(/(\d+)\s*-\s*(\d+)/);
        if (!match) {
          return;
        }
        var min = parseInt(match[1], 10);
        var max = parseInt(match[2], 10);
        if (inputs[key] < min) {
          dist += min - inputs[key];
        } else if (inputs[key] > max) {
          dist += inputs[key] - max;
        }
      });
      if (dist < bestDist) {
        bestDist = dist;
        best = size;
      }
    });

    recoEl.textContent = best || '';
    if (best) {
      sgSelectSize(best, true);
    }
  }

  function sgSelectSize(value, fromCalc) {
    var guide = sgEl();
    // sync the quick-add selection so Add to bag uses it
    if (qa.sizeOptionIndex > -1) {
      qa.selections[qa.sizeOptionIndex] = value;
      qaRefreshOptionStates();
      qaSyncVariant();
    }
    guide.querySelectorAll('[data-fc-sg-sizes] .fc-qa__pill').forEach(function(pill) {
      pill.classList.toggle('is-selected', pill.dataset.value === value);
    });
    if (!fromCalc) {
      guide.querySelector('[data-fc-sg-reco]').textContent = value;
    }
  }

  function openSizeGuide() {
    var guide = sgEl();
    if (!guide || !qa.product || qa.sizeOptionIndex < 0) {
      return;
    }
    // size pills come from the current quick-add product
    var sizes = guide.querySelector('[data-fc-sg-sizes]');
    sizes.innerHTML = '';
    (qa.product.options[qa.sizeOptionIndex].values || []).forEach(function(rawValue) {
      var value = optionValueName(rawValue);
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'fc-qa__pill';
      pill.dataset.value = value;
      pill.textContent = value;
      pill.classList.toggle('is-selected', qa.selections[qa.sizeOptionIndex] === value);
      sizes.appendChild(pill);
    });
    // panel background = featured image of the active quick-add product
    var bg = guide.querySelector('[data-fc-sg-bg]');
    if (bg) {
      if (qa.product.image) {
        bg.src = qa.product.image;
        bg.parentElement.style.display = '';
      } else {
        bg.removeAttribute('src');
        bg.parentElement.style.display = 'none';
      }
    }

    guide.hidden = false;
    guide.querySelector('.fc-sg__panel').focus();
  }

  function initQuickAdd() {
    var sheet = qaEl();
    if (!sheet || sheet.dataset.fcBound) {
      return;
    }
    sheet.dataset.fcBound = 'true';

    // The overlays are siblings of #CartDrawer, so clicks inside them would
    // bubble to theme.Drawers' window listener, whose guard only spares
    // clicks inside `.drawer` — and it would close the cart drawer.
    // Contain clicks here; handlers on the sheet itself still run.
    sheet.addEventListener('click', function(evt) {
      evt.stopPropagation();
    });

    sheet.addEventListener('click', function(evt) {
      if (evt.target.closest('[data-fc-qa-close]')) {
        closeQuickAdd();
        return;
      }
      if (evt.target.closest('[data-fc-qa-continue]')) {
        // "close this popup when clicked" — back to browsing
        closeQuickAdd();
        document.dispatchEvent(new CustomEvent('cart:close'));
        return;
      }
      if (evt.target.closest('[data-fc-qa-size-help]')) {
        openSizeGuide();
        return;
      }
      var valueBtn = evt.target.closest('[data-fc-qa-option]');
      if (valueBtn) {
        qa.selections[parseInt(valueBtn.dataset.fcQaOption, 10)] = valueBtn.dataset.value;
        qaRefreshOptionStates();
        qaSyncVariant();
        return;
      }
      var qaAddBtn = evt.target.closest('[data-fc-qa-add]');
      if (qaAddBtn) {
        qaAddSelected(qaAddBtn);
      }
    });

    window.addEventListener('resize', function() {
      if (!sheet.hidden) {
        qaPositionSheet();
      }
    });

    // Desktop: the overlay only spans the drawer column, so a click on the
    // page outside it (which also makes theme.Drawers close the cart drawer)
    // must take the quick add down with it. Clicks inside the drawer or the
    // overlays are ignored — the backdrop has its own data-fc-qa-close.
    document.addEventListener('click', function(evt) {
      if (sheet.hidden) {
        return;
      }
      if (
        evt.target.closest &&
        (evt.target.closest('.fc-qa') || evt.target.closest('.fc-sg') || evt.target.closest('.drawer'))
      ) {
        return;
      }
      closeQuickAdd();
    });

    // Swipe (mobile) or drag (desktop) DOWN anywhere on the quick add —
    // including the blurred gradient area above the sheet — closes it.
    // The cart drawer underneath stays open (closeQuickAdd never touches it).
    // When the gesture starts inside the scrollable content it only arms
    // while that content is scrolled to the top.
    var scroller = sheet.querySelector('[data-fc-qa-scroll]');
    var touchStartY = null;

    function canStartDrag(evt) {
      var inScroller = evt.target.closest && evt.target.closest('[data-fc-qa-scroll]');
      return !inScroller || scroller.scrollTop <= 0;
    }

    // iOS-sheet motion: the WHOLE overlay (gradient backdrop + content)
    // follows the finger/cursor 1:1, snaps back with a transition under the
    // threshold, and slides out when released past it.
    function dragFollow(delta) {
      sheet.style.transition = 'none';
      sheet.style.transform = delta > 0 ? 'translateY(' + delta + 'px)' : '';
    }
    function dragRelease(delta) {
      if (delta > 100) {
        // closeQuickAdd animates down from the current dragged position
        closeQuickAdd();
        return;
      }
      sheet.style.transition = 'transform 0.2s ' + QA_EASE;
      sheet.style.transform = '';
      window.setTimeout(function() {
        sheet.style.transition = '';
      }, 220);
    }

    var mouseStartY = null;
    var mouseDragging = false;
    sheet.addEventListener('pointerdown', function(evt) {
      if (evt.pointerType !== 'mouse' || evt.button !== 0 || !canStartDrag(evt)) {
        return;
      }
      mouseStartY = evt.clientY;
      mouseDragging = false;
    });
    sheet.addEventListener('pointermove', function(evt) {
      if (mouseStartY === null || evt.pointerType !== 'mouse') {
        return;
      }
      var delta = evt.clientY - mouseStartY;
      if (!mouseDragging && delta > 8) {
        mouseDragging = true;
        sheet.setPointerCapture(evt.pointerId);
      }
      if (mouseDragging) {
        dragFollow(delta);
      }
    });
    function endMouseDrag(evt) {
      if (mouseStartY === null || evt.pointerType !== 'mouse') {
        return;
      }
      var delta = evt.clientY - mouseStartY;
      mouseStartY = null;
      if (mouseDragging) {
        dragRelease(delta);
      }
    }
    sheet.addEventListener('pointerup', endMouseDrag);
    sheet.addEventListener('pointercancel', endMouseDrag);
    // a drag must not trigger the click underneath it (incl. backdrop close)
    sheet.addEventListener('click', function(evt) {
      if (mouseDragging) {
        mouseDragging = false;
        evt.preventDefault();
        evt.stopPropagation();
      }
    }, true);
    sheet.addEventListener('touchstart', function(evt) {
      touchStartY = canStartDrag(evt) ? evt.touches[0].clientY : null;
    }, { passive: true });
    sheet.addEventListener('touchmove', function(evt) {
      if (touchStartY === null) {
        return;
      }
      dragFollow(evt.touches[0].clientY - touchStartY);
    }, { passive: true });
    sheet.addEventListener('touchend', function(evt) {
      if (touchStartY === null) {
        return;
      }
      var delta = evt.changedTouches[0].clientY - touchStartY;
      touchStartY = null;
      dragRelease(delta);
    });

    var guide = sgEl();
    if (guide && !guide.dataset.fcBound) {
      guide.dataset.fcBound = 'true';
      // same containment as the quick add sheet — see comment above
      guide.addEventListener('click', function(evt) {
        evt.stopPropagation();
      });
      guide.addEventListener('click', function(evt) {
        if (evt.target.closest('[data-fc-sg-close]')) {
          closeSizeGuide();
          return;
        }
        if (evt.target.closest('[data-fc-sg-continue]')) {
          // acts as "back": only the size guide closes, the quick add
          // (and the drawer) stay exactly as they were
          closeSizeGuide();
          return;
        }
        var chartToggle = evt.target.closest('[data-fc-sg-chart-toggle]');
        if (chartToggle) {
          var chart = guide.querySelector('[data-fc-sg-chart]');
          chart.hidden = !chart.hidden;
          chartToggle.setAttribute('aria-expanded', chart.hidden ? 'false' : 'true');
          return;
        }
        var sizePill = evt.target.closest('[data-fc-sg-sizes] .fc-qa__pill');
        if (sizePill) {
          sgSelectSize(sizePill.dataset.value, false);
          return;
        }
        var sgAddBtn = evt.target.closest('[data-fc-sg-add]');
        if (sgAddBtn) {
          qaAddSelected(sgAddBtn);
        }
      });
      guide.querySelectorAll('[data-fc-sg-input]').forEach(function(input) {
        input.addEventListener('input', sgCalcRecommendation);
      });
    }

    document.addEventListener('keydown', function(evt) {
      if (evt.key !== 'Escape') {
        return;
      }
      if (guide && !guide.hidden) {
        closeSizeGuide();
      } else if (!sheet.hidden) {
        closeQuickAdd();
      }
    });
    // The gift popup broadcasts modalOpen before opening — get out of its way
    document.addEventListener('modalOpen', closeQuickAdd);
  }

  function quickAddToCart(btn) {
    if (btn.classList.contains('is-adding')) {
      return;
    }
    btn.classList.add('is-adding');

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: parseInt(btn.dataset.variantId, 10), quantity: 1 })
    })
      .then(function(response) { return response.json(); })
      .then(function(data) {
        btn.classList.remove('is-adding');
        if (data.status && data.status !== 200) {
          alert(data.description || data.message);
          return;
        }
        // theme.CartDrawer listens for this: rebuilds the cart and opens the drawer.
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
      })
      .catch(function() {
        btn.classList.remove('is-adding');
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: the drawer is hidden until opened, so selecting the
     FC Cart Drawer section (or one of its blocks) would look like nothing.
     Open it on select, close on deselect, and re-wire after a section
     re-render (the editor swaps the section DOM, orphaning old bindings). */
  if (window.Shopify && window.Shopify.designMode && !window.__fcCartDrawerEditorBound) {
    window.__fcCartDrawerEditorBound = true;

    var isThisSection = function(evt) {
      return evt.target && evt.target.querySelector && evt.target.querySelector('#CartDrawer.fc-cart-drawer');
    };

    document.addEventListener('shopify:section:select', function(evt) {
      if (isThisSection(evt)) {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    });
    document.addEventListener('shopify:block:select', function(evt) {
      if (evt.target && evt.target.closest && evt.target.closest('.fc-cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    });
    document.addEventListener('shopify:section:deselect', function(evt) {
      if (isThisSection(evt)) {
        document.dispatchEvent(new CustomEvent('cart:close'));
      }
    });
    document.addEventListener('shopify:section:load', function(evt) {
      if (isThisSection(evt)) {
        init();
        if (window.theme && theme.CartDrawer) {
          new theme.CartDrawer();
        }
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    });
  }
})();
